import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";

import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import subcompanyService from "../../services/company/subcompanyService";
import CompanySwitchService from "../../services/company/companySwitchService";
import { S } from "./styles";
import { SubscriberCheckout } from "./PlansPage";

// ── temporary frontend-only storage for "created but not yet activated"
// accounts. There's no backend model for this state yet (see write-up in
// chat) — once one exists, this whole PENDING_* block goes away in favor
// of a real GET/POST to the backend.
const PENDING_KEY = "athena_pending_accounts";

const loadPending = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const savePending = (list) => {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch (e) {
    // ignore quota/serialization errors — non-critical local cache
  }
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AccountsPage() {
  const navigate = useNavigate();

  // ── real, activated companies ──────────────────────────────────────────
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  // ── pending (frontend-only) accounts ───────────────────────────────────
  const [pendingAccounts, setPendingAccounts] = useState(loadPending());

  // ── "new account" form ──────────────────────────────────────────────────
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [newAccountErrors, setNewAccountErrors] = useState({});
  const [savingNewAccount, setSavingNewAccount] = useState(false);

  // ── activation (Activate -> the same Subscriber's Details + Your Plan
  // checkout the Plans tab uses, just pre-filled from the pending account) ──
  const [activatingAccount, setActivatingAccount] = useState(null);

  // ── switch active company ────────────────────────────────────────────────
  const [switchingId, setSwitchingId] = useState(null);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  const handleSwitchCompany = async (companyId) => {
    setSwitchingId(companyId);
    try {
      const res = await CompanySwitchService.switchCompany(companyId);
      if (res.success === 1) {
        window.location.href = "/subscription";
      } else {
        notify(toast.error, res.error || "Failed to switch company");
        setSwitchingId(null);
      }
    } catch (err) {
      notify(
        toast.error,
        (err.response && err.response.data && err.response.data.error) ||
          "Failed to switch company"
      );
      setSwitchingId(null);
    }
  };

  const fetchAccounts = () => {
    setAccountsLoading(true);
    Promise.allSettled([
      subcompanyService.getMyClients(),
      axios.get(API + "company/profile/", { headers: authHeader() }),
    ])
      .then(([accountsResult, profileResult]) => {
        if (accountsResult.status !== "fulfilled") {
          notify(toast.error, "Failed to fetch accounts");
          setAccounts([]);
          return;
        }

        const list = accountsResult.value.data.clients || [];
        const profile =
          profileResult.status === "fulfilled" ? profileResult.value.data : null;
        const alreadyListed =
          profile &&
          list.some(
            (a) => a.relationship_type === "self" || a.company_id === profile.id
          );

        // The company you're actually logged into right now — the backend's
        // /company/my-clients/ only lists managed child companies, so when
        // you're at the root/top of your own company it never appears there
        // on its own. Add it here so "your own account" is always visible,
        // the same way it already is when you've switched into a child.
        // (If the /company/profile/ call itself failed, we just skip this —
        // the managed-companies list from /my-clients/ still renders fine.)
        if (!alreadyListed && profile && profile.id != null) {
          list.unshift({
            company_id: profile.id,
            company_name: profile.company_name,
            contact_name: profile.name,
            currency: profile.currency,
            plan: profile.plan,
            relationship_type: "self",
          });
        }

        setAccounts(list);
      })
      .finally(() => setAccountsLoading(false));
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // ── new account form ────────────────────────────────────────────────────
  const resetNewAccountForm = () => {
    setNewAccountForm({ companyName: "", firstName: "", lastName: "", email: "", phone: "" });
    setNewAccountErrors({});
  };

  const validateNewAccount = () => {
    const errors = {};
    if (!newAccountForm.companyName.trim()) errors.companyName = "Company name is required";
    if (!newAccountForm.firstName.trim()) errors.firstName = "First name is required";
    if (!newAccountForm.lastName.trim()) errors.lastName = "Last name is required";
    if (!newAccountForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!EMAIL_RE.test(newAccountForm.email.trim())) {
      errors.email = "Enter a valid email address";
    }
    return errors;
  };

  const handleNewAccountFieldChange = (field) => (e) => {
    const value = e.target.value;
    setNewAccountForm((prev) => ({ ...prev, [field]: value }));
    if (newAccountErrors[field]) {
      setNewAccountErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleCreateAccountRecord = async (e) => {
    e.preventDefault();
    const errors = validateNewAccount();
    setNewAccountErrors(errors);
    if (Object.keys(errors).length > 0) {
      notify(toast.error, "Please fix the highlighted fields");
      return;
    }

    setSavingNewAccount(true);
    try {
      // Real check against the same endpoint the rest of the checkout uses,
      // so we don't let someone create a pending account with an email
      // that's already registered (which would fail later at activation).
      const res = await axios.post(
        API + "accounting/uservalidation/",
        { email: newAccountForm.email.trim() },
        { headers: authHeader() }
      );
      if (res.data && res.data.success === 0) {
        setNewAccountErrors((prev) => ({
          ...prev,
          email: res.data.response || "This email is already registered",
        }));
        return;
      }
    } catch (error) {
      // Non-blocking network hiccup — allow through, backend will still
      // reject a duplicate email at activation time.
    } finally {
      setSavingNewAccount(false);
    }

    const record = {
      id: `pending-${Date.now()}`,
      companyName: newAccountForm.companyName.trim(),
      firstName: newAccountForm.firstName.trim(),
      lastName: newAccountForm.lastName.trim(),
      email: newAccountForm.email.trim(),
      phone: newAccountForm.phone.trim(),
      createdAt: new Date().toISOString(),
    };
    const next = [...pendingAccounts, record];
    setPendingAccounts(next);
    savePending(next);
    notify(toast.success, "Account added. Click Activate to set up their subscription.");
    setShowNewAccountForm(false);
    resetNewAccountForm();
  };

  const removePendingAccount = (id) => {
    const next = pendingAccounts.filter((a) => a.id !== id);
    setPendingAccounts(next);
    savePending(next);
  };

  // ── activation: the exact same checkout used from the Plans tab ─────────
  if (activatingAccount) {
    return (
      <div style={S.panel}>
        <ToastContainer />
        <SubscriberCheckout
          initialDetails={{
            firstName: activatingAccount.firstName,
            lastName: activatingAccount.lastName,
            email: activatingAccount.email,
            phone: activatingAccount.phone,
            companyName: activatingAccount.companyName,
          }}
          backLabel="Back to Accounts"
          onExit={() => setActivatingAccount(null)}
          onSuccess={() => {
            removePendingAccount(activatingAccount.id);
            fetchAccounts();
          }}
        />
      </div>
    );
  }

  // ── main list view ────────────────────────────────────────────────────────
  return (
    <div style={S.panelWide}>
      <ToastContainer />

      <div style={S.panelHead}>
        <div>
          <h2 style={S.panelTitle}>Accounts</h2>
          <p style={S.panelDesc}>
            Create a Account, then activate them once they're ready to
            subscribe.
          </p>
        </div>
        <button
          type="button"
          style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary }}
          onClick={() => {
            resetNewAccountForm();
            setShowNewAccountForm(true);
          }}
        >
          + New Account
        </button>
      </div>

      {pendingAccounts.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
            Pending Activation
          </h4>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Company</th>
                  <th style={S.th}>Contact</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingAccounts.map((a) => (
                  <tr key={a.id}>
                    <td style={S.td}>{a.companyName}</td>
                    <td style={S.td}>{a.firstName} {a.lastName}</td>
                    <td style={S.td}>{a.email}</td>
                    <td style={S.td}>
                      <span style={{ ...S.badge, ...S.badgeWarning }}>
                        Pending
                      </span>
                    </td>
                    <td style={S.td}>
                      <button
                        type="button"
                        style={S.tableActionBtn}
                        onClick={() => setActivatingAccount(a)}
                      >
                        Activate
                      </button>
                      <button
                        type="button"
                        style={S.tableActionBtnMuted}
                        onClick={() => removePendingAccount(a.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
          Active Companies
        </h4>
        {accountsLoading ? (
          <p style={S.subfootnote}>Loading...</p>
        ) : accounts.length === 0 ? (
          <p style={S.subfootnote}>No active companies yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Company Name</th>
                  <th style={S.th}>Contact Name</th>
                  <th style={S.th}>Currency</th>
                  <th style={S.th}>Plan</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a, idx) => {
                  const isCurrent = a.relationship_type === "self";
                  const isSwitching = switchingId === a.company_id;

                  return (
                    <tr key={a.company_id || a.id || idx}>
                      <td style={S.td}>{a.company_name}</td>
                      <td style={S.td}>{a.contact_name}</td>
                      <td style={S.td}>{a.currency}</td>
                      <td style={S.td}>{a.plan}</td>
                      <td style={S.td}>
                        <span
                          style={{
                            ...S.badge,
                            ...(isCurrent ? S.badgePrimary : S.badgeSuccess),
                          }}
                        >
                          {isCurrent ? "Current Account" : "Active"}
                        </span>
                      </td>
                      <td style={S.td}>
                        <div style={S.rowActionsCell}>
                          {isCurrent && (
                            <button
                              type="button"
                              title="Edit profile"
                              aria-label="Edit profile"
                              style={S.rowEditIconBtn}
                              onClick={() => navigate("/profile")}
                            >
                              <i className="fa-solid fa-pen" />
                            </button>
                          )}
                          {!isCurrent && (
                            <button
                              type="button"
                              title={`Switch to ${a.company_name}`}
                              aria-label={`Switch to ${a.company_name}`}
                              style={S.rowSwitchIconBtn}
                              disabled={isSwitching}
                              onClick={() => handleSwitchCompany(a.company_id)}
                            >
                              <i
                                className={
                                  isSwitching
                                    ? "fa-solid fa-spinner fa-spin"
                                    : "fa-solid fa-right-left"
                                }
                              />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNewAccountForm && (
        <div style={S.modalBackdrop} onClick={() => setShowNewAccountForm(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h5 style={S.modalTitle}>New Account</h5>
              <button
                type="button"
                style={S.modalClose}
                aria-label="Close"
                onClick={() => setShowNewAccountForm(false)}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <form onSubmit={handleCreateAccountRecord}>
              <div style={S.modalBody}>
                <div style={S.formGroup}>
                  <label style={S.formLabel}>Company Name *</label>
                  <input
                    style={{
                      ...S.formInput,
                      ...(newAccountErrors.companyName ? S.formInputError : {}),
                    }}
                    value={newAccountForm.companyName}
                    onChange={handleNewAccountFieldChange("companyName")}
                    autoFocus
                  />
                  {newAccountErrors.companyName && (
                    <p style={S.fieldErrorText}>{newAccountErrors.companyName}</p>
                  )}
                </div>
                <div style={S.formGroup}>
                  <label style={S.formLabel}>First Name *</label>
                  <input
                    style={{
                      ...S.formInput,
                      ...(newAccountErrors.firstName ? S.formInputError : {}),
                    }}
                    value={newAccountForm.firstName}
                    onChange={handleNewAccountFieldChange("firstName")}
                  />
                  {newAccountErrors.firstName && (
                    <p style={S.fieldErrorText}>{newAccountErrors.firstName}</p>
                  )}
                </div>
                <div style={S.formGroup}>
                  <label style={S.formLabel}>Last Name *</label>
                  <input
                    style={{
                      ...S.formInput,
                      ...(newAccountErrors.lastName ? S.formInputError : {}),
                    }}
                    value={newAccountForm.lastName}
                    onChange={handleNewAccountFieldChange("lastName")}
                  />
                  {newAccountErrors.lastName && (
                    <p style={S.fieldErrorText}>{newAccountErrors.lastName}</p>
                  )}
                </div>
                <div style={S.formGroup}>
                  <label style={S.formLabel}>Email *</label>
                  <input
                    type="email"
                    style={{
                      ...S.formInput,
                      ...(newAccountErrors.email ? S.formInputError : {}),
                    }}
                    value={newAccountForm.email}
                    onChange={handleNewAccountFieldChange("email")}
                  />
                  {newAccountErrors.email && (
                    <p style={S.fieldErrorText}>{newAccountErrors.email}</p>
                  )}
                </div>
                <div style={{ ...S.formGroup, marginBottom: 0 }}>
                  <label style={S.formLabel}>Phone</label>
                  <input
                    style={S.formInput}
                    value={newAccountForm.phone}
                    onChange={handleNewAccountFieldChange("phone")}
                  />
                </div>
              </div>
              <div style={S.modalFooter}>
                <button
                  type="button"
                  style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
                  onClick={() => setShowNewAccountForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingNewAccount}
                  style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary }}
                >
                  {savingNewAccount ? "Checking..." : "Create Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountsPage;
